import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { UiAvatarComponent } from '../../atoms/avatar/avatar.component';
import { UiButtonComponent } from '../../atoms/button/button.component';
import {
  UiProfileMenuBuiltInIcon,
  UiProfileMenuItem,
  UiUserProfileMenuVariant,
  UI_PROFILE_MENU_DEFAULT_ITEMS,
  UI_PROFILE_MENU_PORTAL_ITEMS,
} from './user-profile-menu.types';

export type {
  UiProfileMenuItem,
  UiProfileMenuBuiltInIcon,
  UiUserProfileMenuVariant,
};
export { UI_PROFILE_MENU_DEFAULT_ITEMS, UI_PROFILE_MENU_PORTAL_ITEMS };

/**
 * Molécula: tarjeta de perfil con cabecera, datos de usuario, lista de acciones y cierre de sesión.
 * Solo presentación; la app maneja navegación vía outputs.
 *
 * - `system` (default): Mi perfil, Mis notas, Mis tareas.
 * - `portal`: Mi cuenta.
 * Si se pasa `items`, tiene prioridad sobre los defaults de la variante.
 */
@Component({
  selector: 'ui-user-profile-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiAvatarComponent, UiButtonComponent],
  templateUrl: './user-profile-menu.component.html',
  styleUrl: './user-profile-menu.component.scss',
  host: {
    class: 'ui-user-profile-menu-host',
    role: 'dialog',
    '[attr.aria-label]': 'title()',
  },
})
export class UiUserProfileMenuComponent {
  readonly variant = input<UiUserProfileMenuVariant>('system');

  readonly title = input('Perfil de usuario');

  readonly userName = input('Usuario');
  readonly userRole = input('');
  readonly userEmail = input('');

  readonly avatarSrc = input<string | null>(null);
  readonly avatarLabel = input<string | null>(null);
  readonly avatarIconSrc = input<string | null>(null);
  readonly avatarAlt = input('');

  /** Si no se indica, usa los ítems por defecto de `variant`. */
  readonly items = input<readonly UiProfileMenuItem[] | undefined>(undefined);

  readonly logoutLabel = input('Cerrar sesión');

  readonly itemSelect = output<{ id: string }>();
  readonly logout = output<void>();

  protected readonly resolvedItems = computed(() => {
    const custom = this.items();
    if (custom !== undefined) {
      return custom;
    }
    return this.variant() === 'portal'
      ? UI_PROFILE_MENU_PORTAL_ITEMS
      : UI_PROFILE_MENU_DEFAULT_ITEMS;
  });

  protected resolveIcon(item: UiProfileMenuItem): UiProfileMenuBuiltInIcon {
    return item.icon ?? 'user';
  }

  protected onItemClick(id: string): void {
    this.itemSelect.emit({ id });
  }

  protected onLogout(): void {
    this.logout.emit();
  }
}
